import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app'
import { Auth, AuthErrorCodes as FirebaseErrorCodes, getAuth, signOut, updateProfile } from 'firebase/auth'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from '@firebase/auth'
import { config } from '@/utils/config'
import { ErrorCodes } from '@/utils/errorCodes'

interface FirebaseErrorWithCode extends Error {
  code?: string
}

let firebaseApp: FirebaseApp

export function getFirebaseAuth(tenantId: string | null): Auth {
  if (!firebaseApp) {
    const firebaseOptions: FirebaseOptions = {
      apiKey: config().firebase.apiKey,
      authDomain: config().firebase.authDomain,
    }
    firebaseApp = initializeApp(firebaseOptions)
  }
  const firebaseAuth = getAuth(firebaseApp)
  firebaseAuth.tenantId = tenantId

  return firebaseAuth
}

export async function signInWithEmailPassword(tenantId: string, email: string, password: string): Promise<string> {
  const firebaseAuth = getFirebaseAuth(tenantId)
  try {
    const userCredentials = await withRetry(() => {
      return signInWithEmailAndPassword(firebaseAuth, email, password)
    })
    if (!userCredentials.user.emailVerified) {
      throw new Error(FirebaseErrorCodes.UNVERIFIED_EMAIL)
    }

    const result = await userCredentials.user.getIdTokenResult(false)
    await clearLoggedInUserState(tenantId) // we no longer need the logged in state on the client side as we don't use it beyond this point

    return result.token
  } catch (err) {
    console.error(`signInWithEmailAndPassword: ${err}`)
    throw translateFirebaseError(err)
  }
}

async function withRetry<T>(fn: () => Promise<T>) {
  const maxAttempts = 3
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (e) {
      const isRetryableError = extractErrorCode(e) == FirebaseErrorCodes.NETWORK_REQUEST_FAILED
      if (!isRetryableError || attempt >= maxAttempts) {
        throw e
      }
      console.log(`retrying action due to error: ${e}`)
      await new Promise((f) => setTimeout(f, 500))
    }
  }
  throw new Error('withRetry exceeded max attempts but unexpectedly did not return in the loop')
}

export async function clearLoggedInUserState(tenantId: string): Promise<void> {
  try {
    await signOut(getFirebaseAuth(tenantId))
  } catch (e) {
    console.error(`failed to clear user state`, e)
  }
}

export async function signUpWithEmailPassword(
  tenantId: string,
  email: string,
  password: string,
  name: string,
): Promise<void> {
  const firebaseAuth = getFirebaseAuth(tenantId)
  try {
    const userCredentials = await createUserWithEmailAndPassword(firebaseAuth, email, password)
    await updateProfile(userCredentials.user, {
      displayName: name,
      photoURL: '',
    })
    await clearLoggedInUserState(tenantId) // we no longer need the logged in state on the client side as we don't use it beyond this point
  } catch (err) {
    console.error(`signUpWithEmailAndPassword: ${err}`)
    throw translateFirebaseError(err)
  }
}

// Translate firebase error to identity error as we don't want to couple with Firebase.
// Firebase error list https://firebase.google.com/docs/auth/admin/errors
export function translateFirebaseError(error: unknown): ErrorCodes {
  const errorCode = extractErrorCode(error)

  switch (errorCode) {
    case 'email-not-allowed':
      return ErrorCodes.NotAllowed
    case FirebaseErrorCodes.UNVERIFIED_EMAIL:
      return ErrorCodes.UnverifiedEmail
    case FirebaseErrorCodes.INVALID_PASSWORD:
      return ErrorCodes.InvalidEmailOrPassword
    case FirebaseErrorCodes.WEAK_PASSWORD:
      return ErrorCodes.InvalidEmailOrPassword
    case FirebaseErrorCodes.USER_DISABLED:
      return ErrorCodes.UserDisabled
    case FirebaseErrorCodes.USER_DELETED:
      return ErrorCodes.InvalidEmailOrPassword
    case FirebaseErrorCodes.EMAIL_EXISTS:
      return ErrorCodes.EmailPasswordAccountExists
    case FirebaseErrorCodes.NETWORK_REQUEST_FAILED:
      return ErrorCodes.NetworkFailure
    case FirebaseErrorCodes.NEED_CONFIRMATION:
      return ErrorCodes.CredentialExistWithDifferentProvider
    default:
      return ErrorCodes.GenericAuthFailure
  }
}

function extractErrorCode(error: unknown): string | null {
  if (error instanceof Error) {
    const customError = error as FirebaseErrorWithCode

    return extractFirebaseAuthErrorCode(customError.message) || customError.code || customError.message
  }

  return null
}

type FirebaseErrorCode = (typeof FirebaseErrorCodes)[keyof typeof FirebaseErrorCodes]

// Extract Firebase AuthErrorCodes.<ERROR_CODE> from error.message
function extractFirebaseAuthErrorCode(errorMessage: string): string | null {
  if (Object.values(FirebaseErrorCodes).includes(errorMessage as FirebaseErrorCode)) {
    return errorMessage
  }

  const errCode = extractFirebaseRawErrorString(errorMessage)

  return errCode || extractCustomErrorCode(errorMessage)
}

// Extract Firebase raw error message
// It usually in the format: Firebase: <long message> (error-code).
// For example: Firebase: An account already exists with the same email address but different sign-in credentials. Sign in using a provider associated with this email address. (auth/account-exists-with-different-credential).
function extractFirebaseRawErrorString(errorMessage: string): string | null {
  const regex = /^Firebase:(.*)\((.*?)\).$/
  const match = regex.exec(errorMessage)

  return match ? match[2] : null
}

// Extract a custom error code set by Papercut’s code in the before sign-in / before create firebase hooks
function extractCustomErrorCode(errorMessage: string): string | null {
  const regex = /"message":"(.*?)"/
  const match = regex.exec(errorMessage)

  return match ? match[1] : null
}
